import React, { useEffect, useMemo, useRef } from "react";

import { SnackbarDuration, toAutoHideDuration } from "./duration";
import { snackbar } from "./snackbarStore";
import { SnackbarAction } from "./types";

/**
 * Declarative form: renders nothing itself, but raises a snackbar in the shared
 * stack while mounted and takes it down on unmount. Use it when the snackbar is
 * driven by state; for event-driven ones call useSnackbar() instead.
 *
 * Props of the old MUI implementation (anchorOrigin, className, noIcon, ...) are
 * accepted and ignored.
 */
export type SnackbarMountProps = {
  onClose?: () => void;
  isOpen?: boolean;
  message: React.ReactNode;
  variant: "success" | "warning" | "error" | "info";
  /** Omit for the standard 5s; pass false to keep it open until dismissed. */
  duration?: SnackbarDuration;
  actions?: SnackbarAction[];
  "data-qa"?: string;
  /* Accepted for backwards compatibility, no longer has any effect. */
  ClickAwayListenerProps?: any;
  anchorOrigin?: any;
  className?: string;
  contentClassName?: string;
  classes?: { content: string };
  children?: React.ReactNode;
  noIcon?: boolean;
  sx?;
};

function SnackbarMount({
  onClose,
  isOpen,
  message,
  variant,
  duration,
  actions,
  "data-qa": dataQa,
}: SnackbarMountProps) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Set while we take the snackbar down ourselves, so a re-render or unmount
  // does not look like the user dismissing it.
  const isSelfDismissingRef = useRef(false);
  const idRef = useRef<string | null>(null);

  const autoHideDuration = toAutoHideDuration(duration);
  const hasMessage = !!message;
  // String messages get a fresh snackbar (and timer) on change. Element messages
  // are rebuilt inline every render, so they keep one snackbar and get patched.
  const messageKey =
    typeof message === "string" || typeof message === "number" ? message : null;
  const lastMessageRef = useRef(message);

  // Handlers go behind a ref so the snackbar always calls the current closure,
  // and the stored array is rebuilt only when the labels change.
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const actionsKey = JSON.stringify(actions?.map((action) => action.label));
  const stableActions = useMemo(
    () =>
      actionsRef.current?.map((action, index) => ({
        label: action.label,
        closeOnClick: action.closeOnClick,
        dataQa: action.dataQa,
        onClick: () => actionsRef.current?.[index]?.onClick(),
      })),
    [actionsKey]
  );
  const lastActionsRef = useRef(stableActions);

  useEffect(() => {
    if (!isOpen || !hasMessage) return;

    const id = snackbar.show({
      message,
      variant,
      actions: stableActions,
      autoHideDuration,
      dataQa,
      onClose: () => {
        idRef.current = null;
        if (isSelfDismissingRef.current) return;
        onCloseRef.current?.();
      },
    });
    idRef.current = id;
    lastMessageRef.current = message;
    lastActionsRef.current = stableActions;

    return () => {
      isSelfDismissingRef.current = true;
      snackbar.dismiss(id);
      isSelfDismissingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasMessage, variant, autoHideDuration, dataQa, messageKey]);

  // Keeps element messages and action labels current without tearing the
  // snackbar down; only writes when an identity actually changed.
  useEffect(() => {
    if (!idRef.current) return;

    const isMessageStale =
      messageKey === null && !Object.is(lastMessageRef.current, message);
    const areActionsStale = !Object.is(lastActionsRef.current, stableActions);
    if (!isMessageStale && !areActionsStale) return;

    lastMessageRef.current = message;
    lastActionsRef.current = stableActions;
    snackbar.update(idRef.current, { message, actions: stableActions });
  });

  return null;
}

export default SnackbarMount;
